import { BootstrapConfig, FooterProps, RiskifiedBeacon } from 'core/type/BootstrapConfig.types';
import { CommonHeaderProps, CurrencyApiResponse, LanguageApiResponse } from 'core/type/header.types';
import { PerformanceClient } from '@agoda/performance-client';
import { WebAnalyticsModule } from '@agoda/analytics-data-acquisition';
import { IMessagingClient } from '@agoda/messaging-client';
import { LoginParams, UserNotificationModel } from '@agoda/layout/lib/framework/model/layout';
import { Optional } from 'core/type/generic';
import { TopDestinationsGroup } from 'core/type/ActivitiesTopDestinations.types';

export const isWindowDefined = (windowArg = window): boolean => typeof windowArg !== 'undefined';

export const getWindowNavigator = (windowArg = window) => windowArg.navigator;

export const getWindowChrome = (windowArg = window) => windowArg && (windowArg as unknown as { chrome: string }).chrome;

export const getWindowLocation = (windowArg = window) => windowArg.location;

// Do not use this setWindowLocation with Deeplink schema as it doesn't work on Safari WebKit engine
export const setWindowLocation = (location: string, windowArg = window): void => {
    windowArg.location.href = location;
};

export const setWindowLocationHash = (hash: string, windowArg = window): void => {
    windowArg.location.hash = hash;
};

export const setPerformance = (client: PerformanceClient, windowArg = window): void => {
    windowArg.performanceClient = client;
};

export const getHeader = (windowArg = window): CommonHeaderProps => {
    return windowArg.header;
};

export const setHeader = (input: CommonHeaderProps, windowArg = window) => {
    windowArg.header = input;
};

export const getFooter = (windowArg = window): FooterProps => {
    return windowArg.footer;
};

export const setFooter = (input: FooterProps, windowArg = window) => {
    windowArg.footer = input;
};

export const getPerformanceClient = (windowArg = window): PerformanceClient => {
    return windowArg.performanceClient;
};

export const getPerformanceObserver = (windowArg = window): typeof PerformanceObserver => {
    return windowArg.PerformanceObserver;
};

export const getForcedCartGatewayApiUrl = (windowArg = window) => {
    return windowArg.cartGwUrl; // MSPA Extension Tool
};

export const getForcedActivitiesGqlApiUrl = (windowArg = window) => {
    return windowArg.forcedActivitiesGqlApiUrl; // MSPA Extension Tool (future)
};

export const getForcedVehicleGqlApiUrl = (windowArg = window) => {
    return windowArg.forcedVehicleGqlApiUrl; // MSPA Extension Tool (future)
};

export const getForcedGatewayApiUrl = (windowArg = window) => {
    return windowArg.gatewayApiUrl; // MSPA Extension Tool
};

export const getPerformance = (windowArg = window) => {
    return windowArg.performance;
};

export const performanceNow = (windowArg = window) => windowArg.performance.now();
export const hasPerformance = (windowArg = window): boolean => windowArg.performance !== undefined;

export const getRiskifiedBeacon = (windowArg = window): RiskifiedBeacon => {
    return windowArg.RISKX;
};

export const isRiskifiedBeaconEnabled = (windowArg = window): boolean => {
    return windowArg.riskified_beacon_enabled;
};

export const getWebAnalytics = (windowArg = window): WebAnalyticsModule => {
    return windowArg.webAnalytics;
};

export const setWebAnalytics = (input: WebAnalyticsModule, windowArg = window) => {
    windowArg.webAnalytics = input;
};

export const getIsJestTest = (windowArg = window): Optional<boolean> => {
    return windowArg.isJestTest;
};

export const getHistory = (windowArg = window): History => {
    return windowArg.history;
};

export const getAttachEvent = (windowArg = window) => {
    return windowArg.attachEvent;
};

export const setAttachEvent = (event: string, listener: EventListener, windowArg = window): boolean => {
    return windowArg.attachEvent(event, listener);
};

export const addEventListener = (event: string, listener: EventListener, options?: boolean | AddEventListenerOptions, windowArg = window): void => {
    return windowArg.addEventListener(event, listener, options);
};

export const removeEventListener = (event: string, listener: EventListener, options?: boolean | AddEventListenerOptions, windowArg = window): void => {
    return windowArg.removeEventListener(event, listener, options);
};

const EmptyBootstrapConfig = {
    browser: {},
    cms: {},
    currency: {},
    experiments: {},
    language: {},
    feMessagingConfig: {},
    behavior: {
        common: {},
    },
} as BootstrapConfig;

export const getBootstrapConfig = (windowArg = window): BootstrapConfig => {
    if (windowArg.bootstrapConfig) {
        return windowArg.bootstrapConfig;
    }
    return EmptyBootstrapConfig;
};

export const setBootstrapConfig = (input: BootstrapConfig, windowArg = window) => {
    windowArg.bootstrapConfig = input;
};

export const getFeMessaging = (windowArg = window) => {
    return windowArg.feMessaging;
};

export const setFeMessaging = (input: IMessagingClient, windowArg = window) => {
    windowArg.feMessaging = input;
};

export const getMocks = (windowArg = window) => {
    return windowArg.mocks;
};

export const setMocks = (
    input: {
        currencies: CurrencyApiResponse;
        languages: LanguageApiResponse;
        layoutLoginParams: { body: LoginParams };
        userNotificationModel: UserNotificationModel;
        topDestinationsGroup: TopDestinationsGroup;
    },
    windowArg = window,
) => {
    windowArg.mocks = input;
};

export const mockWindowObject = (input: Record<string, unknown>) => {
    return { ...input } as unknown as typeof window;
};

export const getScrollPositionX = (windowArg = window): number => windowArg.scrollX;

export const getScrollPositionY = (windowArg = window): number => windowArg.scrollY;

// Do not use this openWindowUrl with Deeplink schema as it doesn't work on Safari WebKit engine
export const openWindowUrl = (url: string, target?: string, options?: string, windowArg = window): void => {
    windowArg.open(url, target, options);
};

export const getWebkit = (windowArg = window) => windowArg.webkit;

export const getActivitiesInterface = (windowArg = window) => windowArg.ActivitiesInterface;
